import { create } from 'zustand'
import { nanoid } from 'nanoid'

export type ToastVariant = 'success' | 'error' | 'warning' | 'info'

export interface Toast {
  id: string
  variant: ToastVariant
  message: string
  durationMs: number
}

const EXIT_MS = 320

interface ToastState {
  toasts:   Toast[]
  exiting:  string[]
  show:      (message: string, variant?: ToastVariant, durationMs?: number) => void
  dismiss:   (id: string) => void
  _startExit: (id: string) => void
  _remove:   (id: string) => void
}

export const useToastStore = create<ToastState>((set, get) => ({
  toasts:  [],
  exiting: [],

  show: (message, variant = 'info', durationMs = 4500) => {
    const id = nanoid(8)
    set((s) => ({ toasts: [...s.toasts, { id, variant, message, durationMs }] }))
    setTimeout(() => get()._startExit(id), durationMs)
  },

  dismiss: (id) => get()._startExit(id),

  _startExit: (id) => {
    if (get().exiting.includes(id)) return
    set((s) => ({ exiting: [...s.exiting, id] }))
    setTimeout(() => get()._remove(id), EXIT_MS)
  },

  _remove: (id) =>
    set((s) => ({
      toasts:  s.toasts.filter((t) => t.id !== id),
      exiting: s.exiting.filter((x) => x !== id),
    })),
}))

/** Convenience helpers — call these anywhere (not inside React components) */
export const toast = {
  success: (msg: string) => useToastStore.getState().show(msg, 'success'),
  error:   (msg: string) => useToastStore.getState().show(msg, 'error'),
  warning: (msg: string) => useToastStore.getState().show(msg, 'warning'),
  info:    (msg: string) => useToastStore.getState().show(msg, 'info'),
}
